import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { ENQUIRY_STAGES, ENQUIRY_STAGES_BY_KEY, SUB_STATUSES_BY_KEY } from "@/lib/workflow";
import { Plus, Search, Filter, Phone, Mail, Clock, AlertTriangle, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import Pagination, { usePaged } from "@/components/Pagination";

const STAGE_LABELS = ENQUIRY_STAGES_BY_KEY;
const STAGES_ORDER = ENQUIRY_STAGES.map((s) => s.key);

export default function Enquiries() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [stageFilter, setStageFilter] = useState(searchParams.get("stage") || "");
  const [search, setSearch] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const params = stageFilter ? { stage: stageFilter } : {};
      const { data } = await api.get("/enquiry", { params });
      setEnquiries(data);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [stageFilter]);
  useEffect(() => {
    const urlStage = searchParams.get("stage") || "";
    if (urlStage !== stageFilter) setStageFilter(urlStage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setStage = (s) => {
    setStageFilter(s);
    if (s) setSearchParams({ stage: s }); else setSearchParams({});
  };

  const canCreate = ["super_admin", "sales", "sales_coordination", "dealer"].includes(user?.role);
  const filtered = useMemo(() => {
    let out = search.trim()
      ? enquiries.filter((e) =>
          (e.name || "").toLowerCase().includes(search.toLowerCase())
          || (e.phone || "").includes(search)
          || (e.enquiry_id || "").toLowerCase().includes(search.toLowerCase())
        )
      : enquiries;
    out = out.slice().sort((a, b) => {
      const ax = new Date(a.created_at || 0).getTime();
      const bx = new Date(b.created_at || 0).getTime();
      return sortDir === "desc" ? bx - ax : ax - bx;
    });
    return out;
  }, [enquiries, search, sortDir]);
  useEffect(() => { setPage(1); }, [search, stageFilter, sortDir]);
  const pageRows = usePaged(filtered, page, 50);

  const counts = STAGES_ORDER.reduce((acc, s) => {
    acc[s] = enquiries.filter((e) => e.stage === s).length;
    return acc;
  }, {});

  return (
    <DesktopLayout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Enquiries</div>
          <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
            Lead pipeline
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Public form + manual entries. Click a lead to assign or move it forward.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a href="/enquiry" target="_blank" rel="noopener noreferrer"
             className="text-xs font-bold text-slate-600 hover:text-orange-600 border border-slate-300 hover:border-orange-400 rounded-sm px-3 py-2"
             data-testid="enquiries-public-link">
            View public form ↗
          </a>
          {canCreate && (
            <Link to="/enquiries/new" data-testid="enquiries-new-button"
                  className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-4 py-2 rounded-sm">
              <Plus className="w-4 h-4" /> New enquiry
            </Link>
          )}
        </div>
      </div>

      {/* Stage tabs */}
      <div className="flex flex-wrap gap-2 mb-4 border-b border-slate-200 pb-3">
        <StageChip label="All" count={enquiries.length} active={!stageFilter} onClick={() => setStage("")} testId="stage-all" />
        {STAGES_ORDER.map((s) => (
          <StageChip key={s} label={STAGE_LABELS[s].label} count={counts[s] || 0}
            active={stageFilter === s} onClick={() => setStage(stageFilter === s ? "" : s)}
            color={STAGE_LABELS[s].color} bg={STAGE_LABELS[s].bg} testId={`stage-${s}`} />
        ))}
      </div>

      <div className="flex items-center gap-2 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, or enquiry ID"
            data-testid="enquiries-search"
            className="pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm w-full bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" />
        </div>
      </div>

      {error && <div className="text-red-700 bg-red-50 border border-red-200 rounded-sm p-3 mb-4 text-sm">{error}</div>}
      {loading ? (
        <div className="hk-card p-8 text-center text-slate-500">Loading enquiries…</div>
      ) : filtered.length === 0 ? (
        <div className="hk-card p-12 text-center text-slate-500" data-testid="enquiries-empty">
          <Filter className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <div className="font-bold">No enquiries yet.</div>
          <p className="text-sm text-slate-500 mt-1">Share the public form or add one manually.</p>
        </div>
      ) : (
        <div className="hk-card overflow-x-auto">
          <table className="w-full text-sm min-w-[1000px]">
            <thead>
              <tr className="bg-slate-50 hk-tiny-label">
                <th className="px-4 py-3 text-left">Enquiry ID</th>
                <th className="px-4 py-3 text-left">Name & contact</th>
                <th className="px-4 py-3 text-left">Requirements</th>
                <th className="px-4 py-3 text-left">Timeline</th>
                <th className="px-4 py-3 text-left">Assigned</th>
                <th className="px-4 py-3 text-left">Stage</th>
                <th className="px-4 py-3 text-left">
                  <button type="button" onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                    className="inline-flex items-center gap-1 hover:text-slate-800 uppercase text-[10px] font-bold tracking-wide"
                    data-testid="enquiries-sort-date">
                    Received <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((e) => {
                const badge = STAGE_LABELS[e.stage] || STAGE_LABELS.new;
                return (
                  <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => navigate(`/enquiries/${e.id}`)}
                    data-testid={`enquiry-row-${e.enquiry_id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-bold text-slate-800">{e.enquiry_id}</td>
                    <td className="px-4 py-3">
                      <div className="font-bold text-slate-900">{e.name}</div>
                      <div className="text-xs text-slate-500 flex items-center gap-2 mt-0.5">
                        <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> {e.phone}</span>
                        {e.email && <span className="inline-flex items-center gap-1 hidden sm:inline-flex"><Mail className="w-3 h-3" /> {e.email}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(e.requirements || []).slice(0, 3).map((r) => (
                          <span key={r} className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded-sm">{r}</span>
                        ))}
                        {(e.requirements || []).length > 3 && (
                          <span className="text-[10px] text-slate-500">+{e.requirements.length - 3}</span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600 whitespace-nowrap">
                      {e.timeline ? <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {e.timeline}</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {e.assigned_sales_name && <div>Sales: <b>{e.assigned_sales_name}</b></div>}
                      {e.assigned_coordinator_name && <div>Coord: <b>{e.assigned_coordinator_name}</b></div>}
                      {e.linked_quote_number && (
                        <div className="mt-1 inline-flex items-center gap-1 text-[9px] font-black uppercase px-1.5 py-0.5 rounded-sm bg-purple-50 text-purple-800 border border-purple-200" data-testid={`enq-quote-${e.id}`}>
                          Quote: {e.linked_quote_number}
                        </div>
                      )}
                      {!e.assigned_sales_name && !e.assigned_coordinator_name && !e.linked_quote_number && <span className="text-slate-400">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10px] font-black uppercase px-2 py-1 rounded-sm"
                        style={{ color: badge.color, backgroundColor: badge.bg }}>{badge.label}</span>
                      {e.workflow_status && (
                        <div className="mt-1 text-[10px] text-slate-600 font-medium" data-testid={`enq-workflow-${e.id}`}>
                          {e.workflow_status}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">
                      {new Date(e.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination total={filtered.length} pageSize={50} page={page} onChange={setPage} testId="enquiries-pagination" />
        </div>
      )}
    </DesktopLayout>
  );
}

function StageChip({ label, count, active, onClick, color, bg, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-bold border transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
      <span>{label}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${active ? "bg-white text-slate-900" : ""}`}
        style={!active && color ? { color, backgroundColor: bg } : (!active ? { backgroundColor: "#f1f5f9" } : undefined)}>
        {count}
      </span>
    </button>
  );
}
